import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from './session';
import './auth.css';

/**
 * Password reset request (D-005).
 *
 * The reset email links to the hosted WEB reset page — no native deep link in
 * v1. After resetting, Brandon signs into the native app with the new password.
 *
 * The confirmation is deliberately the same whether or not the address has an
 * account. Saying "no account found" would turn this form into a way to
 * discover which addresses exist, which is precisely what disabled public
 * signup is meant to avoid.
 *
 * **A FAILURE IS NOT A DIFFERENT ANSWER ABOUT THE ACCOUNT (H-3).** The screen
 * now distinguishes "the request went through" from "the request did not",
 * which is a fact about Crewlink and the network — never about whether that
 * address exists. Supabase answers known and unknown addresses identically, so
 * the success wording is still the only thing either one produces.
 */
export function ForgotPasswordScreen() {
  const { requestPasswordReset, unconfigured } = useSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setFailure(null);
    // **THE ANSWER IS READ, NOT DISCARDED (H-3).** This used to await the
    // request and then announce success regardless of the outcome, so an
    // email that never left still read as "on its way". Success is now
    // claimed only when the request was actually accepted.
    const result = await requestPasswordReset(email);
    setBusy(false);
    if (result.error !== null) {
      setFailure(result.error);
      return;
    }
    setSent(true);
  }

  return (
    <main className="cl-screen cl-centered auth-screen">
      <header className="auth-header">
        <h1 className="auth-wordmark">Reset your password</h1>
        <p className="auth-subtitle">We will email you a link to choose a new password.</p>
      </header>

      {unconfigured ? (
        <p className="cl-notice" role="status">
          Crewlink is not connected to a server yet, so a reset email cannot be sent from this
          build.
        </p>
      ) : null}

      {failure !== null ? (
        <p className="cl-notice" role="alert">
          {failure}
        </p>
      ) : null}

      {sent ? (
        <p className="cl-notice" role="status">
          If that email address has a Crewlink account, a reset link is on its way. The link opens
          in your web browser.
        </p>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <label className="cl-field">
            <span className="cl-label">Email</span>
            <input
              className="cl-input"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={unconfigured || busy}
            />
          </label>

          <div className="auth-actions">
            <button className="cl-button" type="submit" disabled={unconfigured || busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </div>
        </form>
      )}

      <p className="auth-back">
        <Link className="cl-link" to="/sign-in">
          Back to sign in
        </Link>
      </p>
    </main>
  );
}
